// @ts-ignore
import Mustache from 'mustache';

/**
 * Represents a single search result item from the API
 */
interface SearchResult {
  // Basic fields
  index: string;
  url: string;
  articleSlug: string;
  title: string;
  description: string;

  // Date fields
  converted_date: string;
  date: {
    filterlink: string;
  };

  // Type fields
  article_type: string;
  type_slug: string;
  articleTypeSlug: string;

  // Image fields (transformed from raw API)
  images: {
    medium?: { jpg: string; webp: string };
    large?: { jpg: string; webp: string };
    full?: { jpg: string; webp: string };
    list?: { jpg: string; webp: string };
    list_small?: { jpg: string; webp: string };
    defaultImage: { jpg: string };
  } | null;

  caption: string;

  // Authors (transformed from string[] to object[])
  authors: Array<{ name: string; slug: string }>;

  // Themes (transformed from string[] to object[])
  themes: Array<{ name: string; page: string }>;

  // Optional fields
  contentType?: string;
  aspectRatio?: string;
  imageClasses?: string;
  hideImage?: boolean;
  isVisible?: boolean;
}

/**
 * Response structure from the search API
 */
interface SearchResponse {
  /** Featured/prioritized results */
  lifted: SearchResult[];
  /** Results grouped by content type */
  list: { [type: string]: SearchResult[] };
  /** Miscellaneous results that don't fit other categories */
  other: SearchResult[];
}

/**
 * Cache for Mustache templates used to render search results
 */
interface TemplateCache {
  card: string | null;
  list: string | null;
  other: string | null;
}

/**
 * AddSearch - A search interface handler for displaying and managing search results
 *
 * Features:
 * - Debounced search input handling
 * - Keyboard navigation (arrow keys, Enter, Escape, Tab)
 * - Result caching with TTL
 * - Template-based rendering with Mustache
 * - Event delegation for memory efficiency
 * - Accessibility support (focus management, ARIA)
 */
class AddSearch {
  private searchInput: HTMLInputElement | null = null;
  private resultsContainer: HTMLElement | null = null;
  private searchForm: HTMLFormElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private placeholderElement: HTMLElement | null = null;
  private statusMessageElement: HTMLElement | null = null;
  private currentResults: SearchResult[] = [];
  private currentResponse: SearchResponse | null = null;
  private selectedIndex: number = -1;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private minSearchLength: number = 2;
  private isSearchDisabled: boolean = true;
  private baseUrl: string;
  private language: string;
  private debugMode: boolean = false; // ✅ Add debug flag
  private searchOnSubmitOnly: boolean = false; // ✅ Only search on form submit
  private translations: {
    showAllResults: string;
    showLess: string;
    results: string;
    result: string;
    noResults: string;
    authors: string;
    themes: string;
    other: string;
  };

  private abortController: AbortController | null = null;
  private templateCache: TemplateCache = { card: null, list: null, other: null };
  private resultCache: Map<string, { data: SearchResponse; timestamp: number }> = new Map();
  private cacheTTL: number = 60000; // 1 minute
  private maxCacheSize: number = 50; // ✅ Add max cache size

  private eventListeners: Array<{
    element: HTMLElement | Document;
    event: string;
    handler: (event: Event) => void;
  }> = [];

  // ✅ Add delegated event handlers
  private resultItemsHandler: ((e: Event) => void) | null = null;
  private resultClickHandler: ((e: Event) => void) | null = null;
  private showMoreHandler: ((e: Event) => void) | null = null;
  private cachedResultItems: Element[] | null = null;

  private processingClickEvent: string | boolean = false;

  /**
   * Creates an instance of AddSearch
   * @param {Object} options - Configuration options
   * @param {string} options.language - The language code for translations (defaults to 'fi')
   * @param {boolean} options.debugMode - Enable debug logging to console
   * @param {boolean} options.searchOnSubmitOnly - Only trigger search on form submit, not on input
   */
  constructor(options: { language?: string; debugMode?: boolean; searchOnSubmitOnly?: boolean } = {}) {
    const { language = 'fi', debugMode = false, searchOnSubmitOnly = false } = options;
    this.baseUrl = '/stage-ajax/site-search';
    this.language = document.documentElement.lang || language;
    this.debugMode = debugMode;
    this.searchOnSubmitOnly = searchOnSubmitOnly;
    this.translations = this.getTranslations(this.language);
    this.init();
  }

  /**
   * Initializes the AddSearch instance
   * Sets up DOM elements when ready and registers cleanup on page unload
   * @private
   */
  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupElements());
    } else {
      this.setupElements();
    }

    // ✅ Cleanup on page unload
    window.addEventListener('beforeunload', () => this.destroy());
  }

  /**
   * Sets up DOM element references
   * Finds search input, results container, and form elements
   * @private
   */
  private setupElements(): void {
    this.searchInput = document.querySelector('input[name="q"]') as HTMLInputElement;
    this.resultsContainer = document.getElementById('addsearch-results');
    this.searchForm = document.getElementById('addsearch') as HTMLFormElement;
    this.submitButton = this.searchForm?.querySelector('button[type="submit"]') as HTMLButtonElement;
    this.placeholderElement = document.getElementById('placeholder');
    this.statusMessageElement = document.getElementById('search-status-message');

    if (!this.searchInput || !this.resultsContainer) {
      if (this.debugMode) console.warn('AddSearch: Required elements not found');
      return;
    }

    this.cacheTemplates();
    this.bindEvents();
    this.updatePlaceholderVisibility();
    this.updateSearchDisabledState();
  }

  /**
   * Caches Mustache templates from script tags in the DOM
   * Templates are identified by IDs: search-results-card-template, search-results-list-template, search-results-other-template
   * @private
   */
  private cacheTemplates(): void {
    const cardTemplate = document.getElementById('search-results-card-template');
    const listTemplate = document.getElementById('search-results-list-template');
    const otherTemplate = document.getElementById('search-results-other-template');

    this.templateCache.card = cardTemplate?.textContent || null;
    this.templateCache.list = listTemplate?.textContent || null;
    this.templateCache.other = otherTemplate?.textContent || null;

    if (this.debugMode) {
      console.log('Templates cached:', {
        card: !!this.templateCache.card,
        list: !!this.templateCache.list,
        other: !!this.templateCache.other
      });
    }
  }

  /**
   * Escapes HTML characters to prevent XSS attacks
   * @param {string} text - The text to escape
   * @returns {string} HTML-safe string
   * @private
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Adds a search result to the cache with automatic eviction when size limit is reached
   * Implements FIFO (First In, First Out) eviction strategy
   * @param {string} query - The search query to cache
   * @param {SearchResponse} data - The search response data
   * @returns {void}
   * @private
   */
  private addToCache(query: string, data: SearchResponse): void {
    if (this.resultCache.size >= this.maxCacheSize) {
      const firstKey = this.resultCache.keys().next().value!;
      this.resultCache.delete(firstKey);
    }
    this.resultCache.set(query, { data, timestamp: Date.now() });
  }

  /**
   * Adds an event listener and tracks it for cleanup
   * @param {HTMLElement | Document} element - The element to attach the listener to
   * @param {string} event - The event name
   * @param {Function} handler - The event handler function
   * @returns {void}
   * @private
   */
  private addEventListener(
    element: HTMLElement | Document,
    event: string,
    handler: (event: Event) => void
  ): void {
    element.addEventListener(event, handler);
    this.eventListeners.push({ element, event, handler });
  }

  /**
   * Binds all event listeners for search functionality
   * Includes input, submit, keyboard navigation, and focus events
   * @private
   */
  private bindEvents(): void {
    if (!this.searchInput || !this.resultsContainer || !this.searchForm) return;

    this.addEventListener(this.searchInput, 'input', (e) => {
      const query = (e.target as HTMLInputElement).value.trim();
      this.updatePlaceholderVisibility();
      this.updateSearchDisabledState();
      if (!this.searchOnSubmitOnly) {
        this.handleSearch(query);
      }
    });

    this.addEventListener(this.searchForm, 'submit', (e) => {
      e.preventDefault();
      const query = this.searchInput?.value.trim();
      if (query && !this.isSearchDisabled) {
        this.performSearch(query);
      }
    });

    this.addEventListener(this.searchInput, 'keydown', (e) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Enter') {
        keyEvent.preventDefault();
        this.searchForm?.requestSubmit();
      } else {
        this.handleKeyNavigation(keyEvent);
      }
    });

    this.addEventListener(document, 'keydown', (e) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Escape' && this.resultsContainer && this.resultsContainer.style.display !== 'none') {
        this.hideResults();
        this.searchInput?.blur();
      }
    });

    this.addEventListener(document, 'keydown', (e) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Tab' && this.resultsContainer && this.resultsContainer.style.display !== 'none') {
        this.handleTabTrapping(keyEvent);
      }
    });

    this.addEventListener(this.searchInput, 'focus', () => {
      this.hidePlaceholder();
      if (this.currentResults.length > 0) {
        this.showResults();
      }
    });

    this.addEventListener(this.searchInput, 'blur', () => {
      this.updatePlaceholderVisibility();
    });
  }

  /**
   * Updates placeholder visibility based on input state
   * Shows placeholder if input is empty, hides if input has content or is focused
   * @private
   */
  private updatePlaceholderVisibility(): void {
    if (!this.placeholderElement || !this.searchInput) return;

    const isEmpty = !this.searchInput.value.trim();
    const hasFocus = document.activeElement === this.searchInput;

    if (isEmpty && !hasFocus) {
      this.showPlaceholder();
    } else {
      this.hidePlaceholder();
    }
  }

  /**
   * Shows the placeholder element
   * @private
   */
  private showPlaceholder(): void {
    if (!this.placeholderElement) return;
    this.placeholderElement.style.display = '';
  }

  /**
   * Hides the placeholder element
   * @private
   */
  private hidePlaceholder(): void {
    if (!this.placeholderElement) return;
    this.placeholderElement.style.display = 'none';
  }

  /**
   * Updates the submit button disabled state based on query length
   * @private
   */
  private updateSearchDisabledState(): void {
    if (!this.searchInput) return;

    const query = this.searchInput.value.trim();
    this.isSearchDisabled = query.length < this.minSearchLength;
    if (this.submitButton) {
      this.submitButton.disabled = this.isSearchDisabled;
    }
  }

  /**
   * Updates the status message for screen readers with the number of search results
   * @param {number} count - The number of search results found
   * @private
   */
  private updateStatusMessage(count: number): void {
    if (!this.statusMessageElement) return;

    let message = '';
    if (count === 0) {
      message = this.translations.noResults;
    } else if (count === 1) {
      message = `1 ${this.translations.result}`;
    } else {
      message = `${count} ${this.translations.results}`;
    }

    this.statusMessageElement.textContent = message;
  }

  /**
   * Handles search input with debouncing
   * Waits 300ms after user stops typing before performing search
   * @param {string} query - The search query string
   * @returns {void}
   * @private
   */
  private handleSearch(query: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (this.isSearchDisabled) {
      this.hideResults();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  /**
   * Fetches search results from the API with caching
   * Checks cache first (1 minute TTL), then makes API request if needed
   * Automatically cancels previous in-flight requests
   * @param {string} query - The search query string
   * @returns {Promise<SearchResponse>} Promise resolving to search response data
   * @throws {Error} if the API request fails
   * @private
   */
  private async fetchResults(query: string): Promise<SearchResponse> {
    // Check cache first
    const cached = this.resultCache.get(query);
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      if (this.debugMode) console.log('Returning cached result for:', query);
      return cached.data;
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const queryParams = {
      term: query,
      'stage-language': this.language,
    };

    const url = `${this.baseUrl}?${new URLSearchParams(queryParams).toString()}`;

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Search request failed: ${response.status} ${response.statusText} - ${text}`);
      }

      const jsonResponse = await response.json();

      // ✅ Validate response structure
      if (!jsonResponse || typeof jsonResponse !== 'object') {
        throw new Error('Invalid response format from server');
      }

      this.addToCache(query, jsonResponse);

      if (this.debugMode) console.log('Search results:', jsonResponse);

      return jsonResponse;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      console.error('AddSearch: Error fetching results:', error);
      throw error;
    }
  }

  /**
   * Processes search data and displays results
   * Combines all result types and updates the current state
   * @param {string} query - The search query string
   * @returns {Promise<void>}
   * @private
   */
  private async searchData(query: string): Promise<void> {
    try {
      const results = await this.fetchResults(query);

      const allResults: SearchResult[] = [
        ...(Array.isArray(results.lifted) ? results.lifted : []),
        ...Object.values(results.list || {}).flat(),
        ...(Array.isArray(results.other) ? results.other : [])
      ];

      if (allResults.length === 0) {
        this.showNoResults();
        return;
      }

      this.currentResults = allResults;
      this.currentResponse = results;
      this.selectedIndex = -1;
      this.displayResults(results, query);
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('AddSearch: Error processing search:', error);
        this.showNoResults();
      }
    }
  }

  /**
   * Initiates a search operation
   * Shows loading state and triggers data fetching
   * @param {string} query - The search query string
   * @returns {void}
   * @private
   */
  private performSearch(query: string): void {
    if (!this.resultsContainer) return;

    this.resultsContainer.className = 'addsearch-results loading container';
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.style.display = 'block';

    this.searchData(query);
  }

  /**
   * Displays search results in the UI
   * Renders lifted results, grouped results, and other results using templates
   * @param {SearchResponse} results - The search response data
   * @param {string} query - The search query string (currently unused but kept for potential future use)
   * @returns {void}
   * @private
   */
  private displayResults(results: SearchResponse, query: string): void {
    if (!this.resultsContainer) return;

    this.resultsContainer.className = 'addsearch-results container';

    const hasListResults = results.list && Object.keys(results.list).length > 0;
    const hasOtherResults = results.other && results.other.length > 0;

    if (results.lifted.length === 0 && !hasListResults && !hasOtherResults) {
      this.showNoResults();
      return;
    }

    // Calculate total number of results
    const allResults: SearchResult[] = [
      ...(Array.isArray(results.lifted) ? results.lifted : []),
      ...Object.values(results.list || {}).flat(),
      ...(Array.isArray(results.other) ? results.other : [])
    ];
    const totalResults = allResults.length;

    const htmlParts: string[] = [];

    if (results.lifted.length > 0) {
      htmlParts.push(this.buildNewsCardsHTML(results.lifted));
    }

    if (hasListResults) {
      htmlParts.push(this.buildGroupedResultsHTML(results.list, results.lifted.length));
    }

    if (hasOtherResults) {
      htmlParts.push(this.buildOtherResultsHTML(results.other));
    }

    this.resultsContainer.innerHTML = `
      <div class="addsearch-results-list">
        ${htmlParts.join('')}
      </div>
    `;

    this.updateStatusMessage(totalResults);

    this.cachedResultItems = null; // ✅ Reset cache
    this.showResults();
    this.bindResultEvents();
  }

  /**
   * Builds HTML for featured/lifted news cards
   * @param {SearchResult[]} results - Array of search results to display as cards
   * @returns {string} HTML string for the news cards grid
   * @private
   */
  private buildNewsCardsHTML(results: SearchResult[]): string {
    if (!this.templateCache.card) {
      console.warn('Card template not found, falling back to default rendering');
      return '';
    }

    const rendered = Mustache.render(this.templateCache.card, { hits: results });
    return `<div class="addsearch-news-cards grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">${rendered}</div>`;
  }

  /**
   * Builds HTML for grouped results by content type
   * Each type shows first 3 results with an option to expand
   * @param {Object.<string, SearchResult[]>} groupedResults - Results grouped by content type
   * @param {number} liftedCount - Number of lifted results (for index calculation)
   * @returns {string} HTML string for all grouped results
   * @private
   */
  private buildGroupedResultsHTML(groupedResults: { [type: string]: SearchResult[] }, liftedCount: number): string {
    if (!this.templateCache.list) {
      console.warn('List template not found, falling back to default rendering');
      return '';
    }

    if (!groupedResults || Object.keys(groupedResults).length === 0) {
      return '';
    }

    return Object.entries(groupedResults).map(([type, typeResults]) => {
      return this.renderTypeGroup(type, typeResults);
    }).join('');
  }

  /**
   * Builds HTML for "other" results category
   * Used for miscellaneous content that doesn't fit other categories
   * @param {SearchResult[]} results - Array of other search results
   * @returns {string} HTML string for the other results section
   * @private
   */
  private buildOtherResultsHTML(results: SearchResult[]): string {
    if (!this.templateCache.other) {
      console.warn('Other template not found, falling back to list template');
      return '';
    }

    const rendered = results
      .map(result => Mustache.render(this.templateCache.other!, result))
      .join('');

    return `
      <div class="addsearch-type-group addsearch-other-group">
        <div class="addsearch-type-header">
          <div class="flex flex-row justify-between items-center">
            <h2 class="addsearch-type-title text-lg">${this.escapeHtml(this.translations.other)}</h2>
            <span class="addsearch-type-count text-lg">(${results.length})</span>
          </div>
        </div>
        <div class="addsearch-type-results">
          <div class="addsearch-results-grid">
            ${rendered}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders a single type group with collapsible "show more" functionality
   * Initially displays first 3 results, with option to expand to show all
   * @param {string} type - The content type name
   * @param {SearchResult[]} allResultsForType - All results for this content type
   * @returns {string} HTML string for the type group
   * @private
   */
  private renderTypeGroup(type: string, allResultsForType: SearchResult[]): string {
    if (!this.templateCache.list) return '';

    const resultsToShow = allResultsForType.slice(0, 3);
    const hasMoreResults = allResultsForType.length > 3;

    const renderedResults = resultsToShow
      .map(result => Mustache.render(this.templateCache.list!, result))
      .join('');

    // ✅ Escape HTML to prevent XSS
    return `
      <div class="addsearch-type-group">
        <div class="addsearch-type-header pb-10">
          <div class="flex flex-row justify-between items-center">
            <h2 class="addsearch-type-title text-lg">${this.escapeHtml(type)}</h2>
            <span class="addsearch-type-count text-lg">(${allResultsForType.length})</span>
          </div>
           ${hasMoreResults ? `
            <div class="addsearch-show-more">
              <button class="addsearch-show-more-btn text-md text-primary" data-type="${this.escapeHtml(type)}" data-expanded="false">
                ${this.escapeHtml(this.translations.showAllResults)}
              </button>
            </div>
          ` : ''}
        </div>
        <div class="addsearch-type-results">
          <div class="addsearch-results-grid">
            ${renderedResults}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Displays a "no results" message
   * @private
   */
  private showNoResults(): void {
    if (!this.resultsContainer) return;

    this.resultsContainer.className = 'addsearch-results container';
    this.resultsContainer.innerHTML = `
      <p class="addsearch-no-results">${this.escapeHtml(this.translations.noResults)}</p>
    `;
    this.updateStatusMessage(0);
    this.showResults();
  }

  /**
   * Shows the results container and adds overlay class to body
   * @private
   */
  private showResults(): void {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'block';
      document.body.classList.add('overlay-open');
    }
  }

  /**
   * Hides the results container and removes overlay class from body
   * Resets selected index
   * @private
   */
  private hideResults(): void {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
      document.body.classList.remove('overlay-open');
    }
    this.selectedIndex = -1;
  }

  /**
   * Binds event handlers to result items using event delegation
   * Handles mouseenter and click events efficiently
   * @private
   */
  private bindResultEvents(): void {
    if (!this.resultsContainer) return;

    // Remove old delegated listeners if they exist
    if (this.resultItemsHandler) {
      this.resultsContainer.removeEventListener('mouseenter', this.resultItemsHandler, true);
    }
    if (this.resultClickHandler) {
      this.resultsContainer.removeEventListener('click', this.resultClickHandler);
    }

    // Event delegation for mouseenter (with capture phase for better performance)
    this.resultItemsHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const item = target.closest('.addsearch-result-item') as HTMLElement;
      if (item) {
        const items = this.resultsContainer!.querySelectorAll('.addsearch-result-item');
        const index = Array.from(items).indexOf(item);
        if (index !== -1) {
          this.selectedIndex = index;
          this.updateSelection();
        }
      }
    };

    // Event delegation for click
    this.resultClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const item = target.closest('.addsearch-result-item') as HTMLElement;
      if (item) {
        e.preventDefault();
        const link = item.querySelector('a.addsearch-result-link') as HTMLAnchorElement;
        if (link) {
          const id = item.dataset.searchId || '';
          const position = item.dataset.searchPosition || '';
          const url = link.href;
          const term = this.searchInput?.value.trim() || '';

          this.sendClickEvent({ url, id, position, term });
        }
      }
    };

    this.resultsContainer.addEventListener('mouseenter', this.resultItemsHandler, true);
    this.resultsContainer.addEventListener('click', this.resultClickHandler);

    this.bindShowMoreEvents();
  }

  /**
   * Send click event to server
   *
   * @param {Object} data - Data related to the click event
   * @param {string} data.url - The URL of the clicked result
   * @param {string} data.id - The ID of the clicked result
   * @param {string} data.position - The position of the clicked result in the list
   * @param {string} data.term - The search term used for the query
   * @return {boolean} true if event was sent, false if already processing a click event
   * @private
   */
  private sendClickEvent(data: { url: string, id: string, position: string, term: string }) {
    if(this.processingClickEvent) {
      return;
    }

    this.processingClickEvent = data.id;

    let locationChanging = false;
    const loadTargetPath = () => {
      if (data.url && !locationChanging) {
        this.processingClickEvent = false;
        locationChanging = true;
        window.location.href = data.url;
      }
    };

    const formData = new FormData();
    formData.append('id', data.id);
    formData.append('position', data.position);
    formData.append('term', data.term);

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      fetch(`${this.baseUrl}?stage-language=${this.language}&context=reportClick`, {
        method: 'POST',
        signal: this.abortController.signal,
        body: formData,
      }).then(() => {
        console.log('[addsearch] Click event reported successfully');
        loadTargetPath();
      }).catch((err) => {
        console.log('[addsearch] Error reporting click event:', err);
        loadTargetPath();
      });

      return true;
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error(`[addsearch] ${err.message}`, err);
      }
    }

    // Navigate to new url after small delay, even if ajax request has not completed
    setTimeout(loadTargetPath, 500);
  }

  /**
   * Handles keyboard navigation through search results
   * Supports ArrowUp, ArrowDown, Enter, and Escape keys
   * @param {KeyboardEvent} e - The keyboard event
   * @returns {void}
   * @private
   */
  private handleKeyNavigation(e: KeyboardEvent): void {
    if (!this.resultsContainer || this.resultsContainer.style.display === 'none') {
      return;
    }

    switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      this.selectedIndex = Math.min(this.selectedIndex + 1, this.currentResults.length - 1);
      this.updateSelection();
      break;

    case 'ArrowUp':
      e.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
      this.updateSelection();
      break;

    case 'Enter':
      e.preventDefault();
      if (this.selectedIndex >= 0 && this.selectedIndex < this.currentResults.length) {
        const result = this.currentResults[this.selectedIndex];
        window.location.href = result.url;
      }
      break;

    case 'Escape':
      this.hideResults();
      this.searchInput?.blur();
      break;
    }
  }

  /**
   * Handles Tab key trapping within the search interface
   * Keeps focus within search input and results for accessibility
   * @param {KeyboardEvent} e - The keyboard event
   * @returns {void}
   * @private
   */
  private handleTabTrapping(e: KeyboardEvent): void {
    if (!this.resultsContainer || !this.searchInput) return;

    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const searchContainer = this.searchInput.closest('form') || this.searchInput.parentElement;
    if (!searchContainer) return;

    const focusableElements = Array.from(
      searchContainer.querySelectorAll(focusableSelector)
    ).concat(
      Array.from(this.resultsContainer.querySelectorAll(focusableSelector))
    ) as HTMLElement[];

    const visibleFocusableElements = focusableElements.filter(el => {
      return el.offsetParent !== null && !el.hasAttribute('hidden');
    });

    if (visibleFocusableElements.length === 0) return;

    const firstFocusable = visibleFocusableElements[0];
    const lastFocusable = visibleFocusableElements[visibleFocusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (e.shiftKey) {
      if (activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  /**
   * Updates the visual selection state of result items
   * Caches result items for performance optimization
   * @private
   */
  private updateSelection(): void {
    if (!this.cachedResultItems || this.cachedResultItems.length === 0) {
      const nodeList = this.resultsContainer?.querySelectorAll('.addsearch-result-item');
      this.cachedResultItems = nodeList ? Array.from(nodeList) : null;
    }

    if (!this.cachedResultItems) return;

    this.cachedResultItems.forEach((item, index) => {
      item.classList.toggle('focused', index === this.selectedIndex);
    });
  }

  /**
   * Returns translations for the given language
   * Falls back to Finnish if language not found
   * @param {string} lang - Language code ('fi' or 'en')
   * @returns {Object} Translation object with UI strings
   * @private
   */
  private getTranslations(lang: string) {
    const translations: {
      [key: string]: {
        showAllResults: string;
        showLess: string;
        results: string;
        result: string;
        noResults: string;
        authors: string;
        themes: string;
        other: string;
      }
    } = {
      fi: {
        showAllResults: 'Näytä kaikki',
        showLess: 'Näytä vähemmän',
        results: 'tulosta',
        result: 'hakutulos',
        noResults: 'Ei hakutuloksia',
        authors: 'Kirjoittajat:',
        themes: 'Teemat:',
        other: 'Muu sisältö',
      },
      en: {
        showAllResults: 'Show all',
        showLess: 'Show less',
        results: 'results',
        result: 'result',
        noResults: 'No search results',
        authors: 'Authors:',
        themes: 'Themes:',
        other: 'Other content',
      }
    };

    return translations[lang] || translations['fi'];
  }

  /**
   * Binds event handlers for "show more" buttons using event delegation
   * @private
   */
  private bindShowMoreEvents(): void {
    if (!this.resultsContainer) return;

    // Remove old delegated listener if exists
    if (this.showMoreHandler) {
      this.resultsContainer.removeEventListener('click', this.showMoreHandler);
    }

    // Event delegation for show more buttons
    this.showMoreHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('addsearch-show-more-btn')) {
        e.preventDefault();
        const type = target.getAttribute('data-type');
        if (type) {
          this.toggleResultsForType(type);
        }
      }
    };

    this.resultsContainer.addEventListener('click', this.showMoreHandler);
  }

  /**
   * Toggles between showing 3 results and all results for a specific content type
   * @param {string} type - The content type to toggle
   * @returns {void}
   * @private
   */
  private toggleResultsForType(type: string): void {
    if (!this.resultsContainer || !this.currentResponse?.list || !this.templateCache.list) return;

    const allResultsForType = this.currentResponse.list[type];
    if (!allResultsForType) return;

    const typeGroup = this.resultsContainer.querySelector(`[data-type="${CSS.escape(type)}"]`)?.closest('.addsearch-type-group');
    if (!typeGroup) return;

    const resultsGrid = typeGroup.querySelector('.addsearch-results-grid');
    const button = typeGroup.querySelector('.addsearch-show-more-btn') as HTMLElement;

    if (!resultsGrid || !button) return;

    const isExpanded = button.getAttribute('data-expanded') === 'true';
    const resultsToShow = isExpanded ? allResultsForType.slice(0, 3) : allResultsForType;

    const renderedResults = resultsToShow
      .map(result => Mustache.render(this.templateCache.list!, result))
      .join('');

    resultsGrid.innerHTML = renderedResults;

    button.setAttribute('data-expanded', (!isExpanded).toString());
    button.textContent = isExpanded ? this.translations.showAllResults : this.translations.showLess;

    // ✅ Clear cached items since DOM changed
    this.cachedResultItems = null;
  }

  /**
   * Cleans up all resources, event listeners, and references
   * Should be called before removing the AddSearch instance
   * Automatically called on page unload
   * @public
   */
  public destroy(): void {
    // Clear timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Abort any pending requests
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Remove tracked event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    // Remove delegated event listeners
    if (this.resultsContainer) {
      if (this.resultItemsHandler) {
        this.resultsContainer.removeEventListener('mouseenter', this.resultItemsHandler, true);
      }
      if (this.resultClickHandler) {
        this.resultsContainer.removeEventListener('click', this.resultClickHandler);
      }
      if (this.showMoreHandler) {
        this.resultsContainer.removeEventListener('click', this.showMoreHandler);
      }
    }

    // Clear caches
    this.resultCache.clear();
    this.templateCache = { card: null, list: null, other: null };
    this.cachedResultItems = null;

    // Clear current results
    this.currentResults = [];
    this.currentResponse = null;
    this.selectedIndex = -1;

    // Clear references
    this.resultItemsHandler = null;
    this.resultClickHandler = null;
    this.showMoreHandler = null;
    this.searchInput = null;
    this.resultsContainer = null;
    this.searchForm = null;
    this.submitButton = null;
    this.placeholderElement = null;
    this.statusMessageElement = null;

    if (this.debugMode) console.log('AddSearch destroyed and cleaned up');
  }
}

/**
 * Initialize AddSearch singleton instance when the module is loaded
 * Can be imported and used throughout the application
 */
const addSearch = new AddSearch({searchOnSubmitOnly: true});

export default addSearch;
